import type * as React from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useMutation, useSubscription } from '@apollo/react-hooks';
import { ADD_ITEM, MERGE_ITEM_TO_CART, REMOVE_ALL_ITEMS, REMOVE_ITEM, subscription, UPDATE_QUANTITY } from 'gql/query';
import { Action, Index } from 'hooks/models';
import type { CartItem, CartItemSubscription, Product, Variant } from 'types';

import useActivityLogger from '../hooks/useActivityLogger';

import { useStoreDetails } from './store-context';

interface IUpdateQuantity {
  cardItem: CartItem;
  quantity: number;
}

interface IAddToBasket {
  product: Product;
  variant: Variant;
  quantity: number;
}
interface IBasketContext {
  merge: (newItems: CartItem[]) => Promise<void>;
  updateQuantity: (item: IUpdateQuantity) => Promise<void>;
  add: (item: IAddToBasket) => Promise<void>;
  remove: (item: CartItem) => Promise<void>;
  removeAll: (session: OT.Session) => Promise<void>;
  cartItems: CartItem[];
}

interface IBasketContextProvider {
  agentName: string;
  session: OT.Session;
  children: React.ReactNode;
}

const BasketContext = createContext<IBasketContext>({} as IBasketContext);

const BasketContextProvider: React.FC<IBasketContextProvider> = ({ children, agentName, session }) => {
  const { log } = useActivityLogger();
  const { name } = useStoreDetails();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  const [mergeItemToCart] = useMutation(MERGE_ITEM_TO_CART);
  const [addCartItem] = useMutation(ADD_ITEM);
  const [UpdateQuantity] = useMutation(UPDATE_QUANTITY);
  const [removeItemFromCart] = useMutation(REMOVE_ITEM);
  const [removeAllItems] = useMutation(REMOVE_ALL_ITEMS);
  const { data } = useSubscription<CartItemSubscription>(subscription, {
    variables: {
      input: {
        conversationId: session ? session.sessionId : null
      }
    }
  });

  useEffect(() => {
    if (data) setCartItems(data.cartItems);
  }, [data]);

  const updateQuantity = async ({ cardItem, quantity }: IUpdateQuantity) => {
    const {
      variantId,
      variant,
      mergedToCart,
      agentId,
      storeId,
      variants,
      productName,
      productId,
      imgUrl,
      price,
      options,
      conversationId
    } = cardItem;
    await UpdateQuantity({
      variables: {
        input: {
          agentId,
          conversationId,
          imgUrl,
          mergedToCart,
          options,
          price,
          productId,
          productName,
          qty: quantity.toString(),
          storeId,
          variant,
          variantId,
          variants
        }
      }
    });
  };

  const merge = async (newItems: CartItem[]) => {
    try {
      await Promise.all(
        newItems.map(
          async (item) =>
            await mergeItemToCart({
              variables: {
                input: {
                  conversationId: item.conversationId,
                  productId: item.productId,
                  variantId: item.variantId
                }
              }
            })
        )
      );
      void log({
        action: Action.CART,
        agentUsername: agentName,
        index: Index.ACTIVITY,
        label: 'MERGED_TO_CART',
        products: newItems.map((item) => ({
          id: item.productId,
          name: item.productName,
          price: item.price,
          quantity: item.qty
        })),
        sessionID: session.sessionId
      });
    } catch (error) {
      void log({
        action: Action.ERROR,
        agentUsername: agentName,
        description: `Failed merge to basket, error message: ${(error as Error).message}`,
        index: Index.ACTIVITY,
        label: 'ERROR',
        sessionID: session.sessionId
      });
      console.error(error);
    }
  };

  const add = async ({ product, variant, quantity }: IAddToBasket) => {
    void (await addCartItem({
      variables: {
        input: {
          agentId: agentName,
          conversationId: session ? session.sessionId : null,
          imgUrl: variant.image ?? product.featuredImage,
          mergedToCart: false,
          options: product.options,
          price: variant.price ?? product.price,
          productId: product.id,
          productName: product.title,
          qty: quantity.toString(),
          storeId: name,
          variant: {
            options: variant.selectedOptions,
            price: variant.price ?? product.price,
            selectedOptions: variant.selectedOptions,
            variantId: variant.id
          },
          variantId: variant.id ?? product.variantId ?? product.id,
          variants: product.variants.map((v) => ({
            id: v.id,
            option1: v.option1,
            option2: v.option2,
            option3: v.option3,
            price: v.price,
            selectedOptions: v.selectedOptions
          }))
        }
      }
    }));
  };

  const remove = async (product: CartItem) => {
    await removeItemFromCart({
      variables: {
        input: {
          conversationId: product.conversationId,
          productId: product.productId,
          variantId: product.variantId
        }
      }
    });
  };
  const removeAll = async (session: OT.Session) => {
    await removeAllItems({
      variables: {
        input: {
          conversationId: session ? session.sessionId : null
        }
      }
    });
  };
  const initialValue = useMemo(
    () => ({ add, cartItems, merge, remove, removeAll, updateQuantity }),
    [add, cartItems, merge, remove, removeAll, updateQuantity]
  );

  return <BasketContext.Provider value={initialValue}>{children}</BasketContext.Provider>;
};

export const useBasket = (): IBasketContext => {
  const { add, remove, removeAll, cartItems, updateQuantity, merge } = useContext(BasketContext)!;
  return { add, cartItems, merge, remove, removeAll, updateQuantity };
};

export default BasketContextProvider;
